import { Plus } from 'lucide-react';
import { Button, type Column } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { ResourceTable } from '../ResourceTable.tsx';
import { useDebouncedValue } from '../useDebouncedValue.ts';
import { usePageContext } from '../usePageContext.ts';
import { useReadyParcels, type HandoverParcel } from './handover.ts';
import { useParcelColumns } from './parcel-columns.tsx';
import styles from './Handover.module.css';

export type ReadyParcelsProps = {
  shipping: ShippingClient;
  handoverId: string;
  search: string;
  busy: boolean;
  onAdd: (trackingNumber: string) => void;
};

const SEARCH_DELAY_MS = 300;

export const ReadyParcels = ({ shipping, handoverId, search, busy, onAdd }: ReadyParcelsProps) => {
  const { t } = usePageContext();
  const settled = useDebouncedValue(search.trim(), SEARCH_DELAY_MS);
  const ready = useReadyParcels(shipping, handoverId, settled);
  const rows = ready.data?.items ?? [];
  const addColumn: Column<HandoverParcel> = {
    id: 'add',
    header: t('handover.add'),
    action: true,
    cell: (row) => (
      <Button
        size="sm"
        variant="outline"
        icon={<Plus size={16} aria-hidden="true" />}
        disabled={busy}
        aria-label={t('handover.addParcel', { trackingNumber: row.trackingNumber })}
        onClick={() => {
          onAdd(row.trackingNumber);
        }}
      >
        {t('handover.add')}
      </Button>
    ),
  };

  return (
    <section className={styles['ready']} aria-label={t('handover.readyTitle')}>
      <h3 className={styles['readyTitle']}>
        {t('handover.readyTitle')}
        {ready.data === undefined ? null : <span className={styles['muted']}>{rows.length}</span>}
      </h3>
      <ResourceTable
        bare
        query={ready}
        columns={[...useParcelColumns(), addColumn]}
        rows={rows}
        rowKey={(row) => row.shipmentId}
        emptyTitle={
          settled === '' ? t('handover.readyNone') : t('handover.readyNoMatch', { search: settled })
        }
      />
    </section>
  );
};
